import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockIcon from "@mui/icons-material/Lock";
import AppsIcon from "@mui/icons-material/Apps";
import GroupIcon from "@mui/icons-material/Group";
import SchoolIcon from "@mui/icons-material/School";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  createTenantBySuperAdmin,
  editTenantBySuperAdmin,
  fetchTenantById,
} from "../../store/slices/Auth/Login/thunk";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";

const modeDefaults: Record<
  string,
  { parentLabel: string; schoolLabel: string; childLabel: string; canAddChild: boolean }
> = {
  school: {
    parentLabel: "Parent",
    schoolLabel: "School",
    childLabel: "Child",
    canAddChild: true,
  },
  company: {
    parentLabel: "Employee",
    schoolLabel: "Company",
    childLabel: "Dependent",
    canAddChild: false,
  },
};

const fieldIconStyle = {
  color: "#2196F3",
  fontSize: 20,
};

const CreateTenant = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    _id: "",
    tenantName: "",
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    mode: "school",
    parentLabel: "Parent",
    schoolLabel: "School",
    childLabel: "Child",
    canAddChild: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isCompanyMode = form.mode === "company";
  const dynamicSchoolLabelTitle = isCompanyMode ? "Company Label *" : "School Label *";
  const dynamicParentLabelTitle = isCompanyMode ? "Employee Label *" : "Parent Label *";
  const dynamicChildLabelTitle = isCompanyMode ? "Dependent Label *" : "Child Label *";

  useEffect(() => {
    const loadTenant = async () => {
      if (!id) return;
      const response: any = await dispatch(fetchTenantById(id));
      if (response?.success && response?.data) {
        setForm({
          _id: response.data._id,
          tenantName: response.data.tenantName || "",
          adminName: response.data.adminName || "",
          adminEmail: response.data.adminEmail || "",
          adminPassword: "",
          mode: response.data?.tenantAppConfig?.mode || "school",
          parentLabel: response.data?.tenantAppConfig?.labels?.parent || "Parent",
          schoolLabel: response.data?.tenantAppConfig?.labels?.school || "School",
          childLabel: response.data?.tenantAppConfig?.labels?.child || "Child",
          canAddChild:
            typeof response.data?.tenantAppConfig?.features?.canAddChild === "boolean"
              ? response.data.tenantAppConfig.features.canAddChild
              : true,
        });
      } else {
        toast.error(response?.message || "Failed to load tenant");
      }
    };
    loadTenant();
  }, [dispatch, id]);

  const onChange = (key: string, value: string | boolean) => {
    if (key === "mode" && typeof value === "string") {
      const selectedModeDefaults = modeDefaults[value] || modeDefaults.school;
      setForm((prev) => ({
        ...prev,
        mode: value,
        parentLabel: selectedModeDefaults.parentLabel,
        schoolLabel: selectedModeDefaults.schoolLabel,
        childLabel: selectedModeDefaults.childLabel,
        canAddChild: selectedModeDefaults.canAddChild,
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreateTenant = async () => {
    const newErrors: Record<string, string> = {};

    if (!form.tenantName.trim()) newErrors.tenantName = "Tenant Name is required";
    if (!form.adminName.trim()) newErrors.adminName = "Admin Name is required";
    if (!form.adminEmail.trim()) {
      newErrors.adminEmail = "Admin Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.adminEmail)) {
      newErrors.adminEmail = "Enter a valid email address";
    }
    if (!isEdit && !form.adminPassword) {
      newErrors.adminPassword = "Password is required";
    } else if (!isEdit && form.adminPassword.length < 6) {
      newErrors.adminPassword = "Password must be at least 6 characters";
    }
    if (!form.mode) newErrors.mode = "Mode is required";
    if (!form.parentLabel.trim()) newErrors.parentLabel = "Parent/Employee label is required";
    if (!form.schoolLabel.trim()) newErrors.schoolLabel = "School/Company label is required";
    if (!form.childLabel.trim()) newErrors.childLabel = "Child/Dependent label is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields!");
      return;
    }

    setErrors({});

    setLoading(true);
    const payload = {
      ...form,
      tenantName: form.tenantName.trim(),
      adminName: form.adminName.trim(),
      adminEmail: form.adminEmail.trim(),
      adminPassword: form.adminPassword,
      mode: form.mode,
      labels: {
        parent: form.parentLabel.trim(),
        school: form.schoolLabel.trim(),
        child: form.childLabel.trim(),
      },
      features: {
        canAddChild: form.canAddChild,
        showChildSection: form.canAddChild,
      },
    };
    const response: any = isEdit
      ? await dispatch(editTenantBySuperAdmin(payload))
      : await dispatch(createTenantBySuperAdmin(payload));
    if (response?.success) {
      toast.success(
        response.message || (isEdit ? "Tenant updated successfully." : "Tenant created successfully.")
      );
      setErrors({});
      setForm({
        _id: "",
        tenantName: "",
        adminName: "",
        adminEmail: "",
        adminPassword: "",
        mode: "school",
        parentLabel: "Parent",
        schoolLabel: "School",
        childLabel: "Child",
        canAddChild: true,
      });
      navigate("/tenant-list");
    } else {
      toast.error(response?.message || (isEdit ? "Failed to update tenant." : "Failed to create tenant."));
    }
    setLoading(false);
  };

  const renderFieldWithIcon = (
    icon: React.ReactNode,
    label: string,
    required: boolean,
    field: React.ReactNode
  ) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          mt: 2.5,
          width: 40,
          height: 40,
          borderRadius: "10px",
          backgroundColor: "#E3F2FD",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#374151",
            fontSize: "0.85rem",
            mb: 0.75,
            mt: 2,
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>*</Box>
          )}
        </Typography>
        {field}
      </Box>
    </Box>
  );

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f8f9fc",
      "& fieldset": {
        borderColor: "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: "#7c6dff",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#7c6dff",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#666",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#7c6dff",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "transparent",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: -50,
          right: -50,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(124,109,255,0.05) 0%, transparent 70%)",
          pointerEvents: "none",
        },
      }}
    >
      <Card
        sx={{
          maxWidth: 960,
          mx: "auto",
          mt: 4,
          mb: 4,
          borderRadius: 4,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          border: "1px solid #eef0f5",
          position: "relative",
          zIndex: 1,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Top bar with back button and breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <IconButton
              onClick={() => navigate("/tenant-list")}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "10px",
                width: 36,
                height: 36,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18, color: "#555" }} />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
              Tenant Management &gt; Create Tenant
            </Typography>
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1a1a2e",
              mb: 0.5,
              mt: 1,
            }}
          >
            {isEdit
              ? "Update Tenant (Super Admin)"
              : "Create Tenant (Super Admin)"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            Add a new tenant to the Van Wala platform.
          </Typography>

          {/* Form Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
              },
              gap: 3,
            }}
          >
            {/* Tenant Name */}
            {renderFieldWithIcon(
              <BusinessIcon sx={fieldIconStyle} />,
              "Tenant Name",
              true,
              <TextField
                fullWidth
                placeholder="Enter tenant name"
                value={form.tenantName}
                onChange={(e) => {
                  onChange("tenantName", e.target.value);
                  if (errors.tenantName) setErrors((prev) => ({ ...prev, tenantName: "" }));
                }}
                error={!!errors.tenantName}
                helperText={errors.tenantName}
                sx={textFieldSx}
              />
            )}

            {/* Tenant Admin Name */}
            {renderFieldWithIcon(
              <PersonIcon sx={fieldIconStyle} />,
              "Tenant Admin Name",
              true,
              <TextField
                fullWidth
                placeholder="Enter admin full name"
                value={form.adminName}
                onChange={(e) => {
                  onChange("adminName", e.target.value);
                  if (errors.adminName) setErrors((prev) => ({ ...prev, adminName: "" }));
                }}
                error={!!errors.adminName}
                helperText={errors.adminName}
                sx={textFieldSx}
              />
            )}

            {/* Tenant Admin Email */}
            {renderFieldWithIcon(
              <MailOutlineIcon sx={fieldIconStyle} />,
              "Tenant Admin Email",
              true,
              <TextField
                fullWidth
                type="email"
                placeholder="admin@tenant.com"
                value={form.adminEmail}
                onChange={(e) => {
                  onChange("adminEmail", e.target.value);
                  if (errors.adminEmail) setErrors((prev) => ({ ...prev, adminEmail: "" }));
                }}
                error={!!errors.adminEmail}
                helperText={errors.adminEmail}
                sx={textFieldSx}
              />
            )}

            {/* Tenant Admin Password */}
            {!isEdit
              ? renderFieldWithIcon(
                  <LockIcon sx={fieldIconStyle} />,
                  "Tenant Admin Password",
                  true,
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    placeholder="********"
                    value={form.adminPassword}
                    onChange={(e) => {
                      onChange("adminPassword", e.target.value);
                      if (errors.adminPassword) setErrors((prev) => ({ ...prev, adminPassword: "" }));
                    }}
                    error={!!errors.adminPassword}
                    helperText={errors.adminPassword}
                    sx={textFieldSx}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ fontSize: 20, color: "#999" }} />
                              ) : (
                                <Visibility sx={{ fontSize: 20, color: "#999" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )
              : (
                  renderFieldWithIcon(
                    <LockIcon sx={fieldIconStyle} />,
                    "Tenant Admin Password",
                    true,
                    <Box />
                  )
                )}

            {/* Tenant Mode */}
            {renderFieldWithIcon(
              <AppsIcon sx={fieldIconStyle} />,
              "Tenant Mode",
              true,
              <TextField
                select
                fullWidth
                value={form.mode}
                onChange={(e) => {
                  onChange("mode", e.target.value);
                  if (errors.mode) setErrors((prev) => ({ ...prev, mode: "" }));
                }}
                error={!!errors.mode}
                helperText={errors.mode}
                sx={textFieldSx}
              >
                <MenuItem value="school">School</MenuItem>
                <MenuItem value="company">Company</MenuItem>
              </TextField>
            )}

            {/* Parent Label / Employee Label */}
            {renderFieldWithIcon(
              <GroupIcon sx={fieldIconStyle} />,
              dynamicParentLabelTitle.replace(" *", ""),
              true,
              <TextField
                fullWidth
                value={form.parentLabel}
                onChange={(e) => {
                  onChange("parentLabel", e.target.value);
                  if (errors.parentLabel) setErrors((prev) => ({ ...prev, parentLabel: "" }));
                }}
                error={!!errors.parentLabel}
                helperText={errors.parentLabel}
                sx={textFieldSx}
              />
            )}

            {/* School Label / Company Label */}
            {renderFieldWithIcon(
              <SchoolIcon sx={fieldIconStyle} />,
              dynamicSchoolLabelTitle.replace(" *", ""),
              true,
              <TextField
                fullWidth
                value={form.schoolLabel}
                onChange={(e) => {
                  onChange("schoolLabel", e.target.value);
                  if (errors.schoolLabel) setErrors((prev) => ({ ...prev, schoolLabel: "" }));
                }}
                error={!!errors.schoolLabel}
                helperText={errors.schoolLabel}
                sx={textFieldSx}
              />
            )}

            {/* Child Label / Dependent Label */}
            {renderFieldWithIcon(
              <ChildCareIcon sx={fieldIconStyle} />,
              dynamicChildLabelTitle.replace(" *", ""),
              true,
              <TextField
                fullWidth
                value={form.childLabel}
                onChange={(e) => {
                  onChange("childLabel", e.target.value);
                  if (errors.childLabel) setErrors((prev) => ({ ...prev, childLabel: "" }));
                }}
                error={!!errors.childLabel}
                helperText={errors.childLabel}
                sx={textFieldSx}
              />
            )}
          </Box>

          {/* Allow adding child records toggle */}
          <Box
            sx={{
              mt: 4,
              display: "flex",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={form.canAddChild}
                  onChange={(e) => onChange("canAddChild", e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#fff",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2196f3",
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                    Allow adding child records
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    If enabled, this tenant can add child records.
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 4,
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/tenant-list")}
              sx={{
                borderColor: "#d0d0d0",
                color: "#555",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                "&:hover": {
                  borderColor: "#999",
                  backgroundColor: "#f9f9f9",
                },
              }}
            >
              Back to Tenant List
            </Button>

            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleCreateTenant}
              disabled={loading}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.2,
                backgroundColor: "#2196f3",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              {loading
                ? "Processing..."
                : isEdit
                ? "Update Tenant"
                : "Create Tenant"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateTenant;
